import { defaultTo, filter, find, without, indexOf } from 'lodash';
import { batchActions } from './utils';
import { setData } from './data';
import { setStatus, unsetStatus } from './status';
import { LOADING, ERROR, selectCategoriesStatus, selectCategoriesState } from './selectors';
import { CATEGORIES_KEY } from '../lib/constants';
import { toJSON, PODCASTERS_SERVICE } from '../lib/Request';
import { Thunk, Category, Subcategory, SelectedCategory } from '../types';

const INITIAL_CATEGORY_STATE = {
  categoryKey: '',
  name: '',
  value: '',
  subcategories: [],
};

export const loadCategories = (): Thunk => async (dispatch, getState, { request }) => {
  const KEY = 'categories';
  const endpoint = `${PODCASTERS_SERVICE}/v0/feeds/categories`;

  /* istanbul ignore next */
  if (selectCategoriesStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [KEY]: LOADING }, 'submission/LOAD_CATEGORIES_REQUEST'));

  try {
    const data: Category[] = await request.get(endpoint).then(toJSON);

    dispatch(
      batchActions(
        [setData({ [KEY]: data }), unsetStatus([KEY])],
        'submission/LOAD_CATEGORIES_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        dispatch(setStatus({ [KEY]: ERROR }, 'submission/LOAD_CATEGORIES_ERROR'));
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};

export const initializeCategory = (categoryKey = 'primaryCategory'): Thunk => (
  dispatch,
  getState,
) => {
  const currentState = selectCategoriesState(getState());
  const nextState = { ...INITIAL_CATEGORY_STATE, categoryKey };

  dispatch(
    setData(
      { [CATEGORIES_KEY]: currentState ? [...currentState, nextState] : [nextState] },
      'submission/INITIALIZE_CATEGORY',
    ),
  );
};

const filterCategories = (categories: SelectedCategory[] | undefined, categoryKey: string) =>
  filter(categories, (c: SelectedCategory) => c.categoryKey !== categoryKey);

const insertCategoryAt = (
  categories: SelectedCategory[] | undefined,
  category: SelectedCategory,
  index: number,
) => {
  const dupedCategories = [...defaultTo(categories, [])];
  dupedCategories[index] = category;
  return dupedCategories;
};

export const setCategory = (categoryKey: string, { name, value }): Thunk => (
  dispatch,
  getState,
) => {
  const currentState = selectCategoriesState(getState());
  let category = find(currentState, { categoryKey }) as SelectedCategory;
  const categoryIndex = indexOf(currentState, category);
  category = { ...category, name, value, subcategories: [] };
  dispatch(
    setData(
      { [CATEGORIES_KEY]: insertCategoryAt(currentState, category, categoryIndex) },
      'submission/SET_CATEGORY',
    ),
  );
};

export const removeCategory = (categoryKey: string): Thunk => (dispatch, getState) => {
  const currentState = selectCategoriesState(getState());
  dispatch(
    setData(
      { [CATEGORIES_KEY]: filterCategories(currentState, categoryKey) },
      'submission/REMOVE_CATEGORY',
    ),
  );
};

export const setSubcategory = (categoryKey: string, subcategory: Subcategory): Thunk => (
  dispatch,
  getState,
) => {
  const currentState = selectCategoriesState(getState());
  let category = find(currentState, { categoryKey }) as SelectedCategory;
  const categoryIndex = indexOf(currentState, category);
  category = { ...category, subcategories: [...category.subcategories, subcategory] };
  dispatch(
    setData(
      { [CATEGORIES_KEY]: insertCategoryAt(currentState, category, categoryIndex) },
      'submission/SET_SUBCATEGORY',
    ),
  );
};

export const removeSubcategory = (categoryKey: string, subcategory: Subcategory): Thunk => (
  dispatch,
  getState,
) => {
  const currentState = selectCategoriesState(getState());
  let category = find(currentState, { categoryKey }) as SelectedCategory;
  const categoryIndex = indexOf(currentState, category);
  category = { ...category, subcategories: without(category.subcategories, subcategory) };
  dispatch(
    setData(
      { [CATEGORIES_KEY]: insertCategoryAt(currentState, category, categoryIndex) },
      'submission/REMOVE_SUBCATEGORY',
    ),
  );
};
